import { assign } from "lodash";
import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ModalControlComponent } from "@medsurf/flat-components";
import { ZodSharedValidator } from "@medsurf/flat-validators";
import { BaseChoiceComponent } from "../../base-question/base-choice/base-choice.component";

@Component({
    selector: 'medsurf-quiz-k-prim-question-choice',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './k-prim-question-choice.component.html',
    styleUrls: ['./k-prim-question-choice.component.scss'],
    standalone: false
})
export class KPrimQuestionChoiceComponent extends BaseChoiceComponent<FlatModels.QuestionEntityModels.KprimeQuestion | FlatModels.QuestionEntityModels.TrueFalseQuestion> {
  @Output() solutionChange = new EventEmitter<{index: number, value: string}>();
  
  override form = new FormGroup(
    {
      label: new FormControl(''),
      solution: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.ChoiceEntityModels.ChoiceSchema.pick({
          label: true,
        }).merge(FlatModels.QuestionEntityModels.KprimeQuestionSolutionSchema)
      ),
      updateOn: 'change'
    }
  );
  
  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
                     public trainingSharedFacade: FlatFacades.TrainingSharedFacade,
                     public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
                     public historySharedFacade: FlatFacades.HistorySharedFacade,
                     public dialogService: DialogService,
                     public translate: TranslateService) {
    super(alertControlFacade, trainingSharedFacade, slideAuthorFacade, historySharedFacade);
    
    // Save Snapshot Success
    this.historySharedFacade.ofActionDispatchedSaveSnapshotsSuccess.pipe(takeUntilDestroyed()).subscribe(() => {
      this.isSubmitted.set(false);
    });
  }

  override updateForm() {
    if (this.index !== undefined && this.question && this.choice) {
      if (this.form && (this.form.value.label !== this.choice.label || this.form.value.solution !== this.question.solutions[this.index])) {
        // Update Data
        this.form.reset({ label: this.choice.label, solution: this.question.solutions[this.index] });
        this.isSubmitted.set(false);
      }
    }
  }

  override onSubmit() {
    if (this.isSubmitted() || this.form.invalid || !this.choice) {
      return;
    }
    this.isSubmitted.set(true);
    
    // Update Choice
    const choice = assign(this.choice, {
      label: this.form.value['label'],
    });

    // Request Update Choice
    this.slideAuthorFacade.requestUpdateChoice(choice, 'history.choice_update').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.form);
        this.isSubmitted.set(false);
      },
      next: () => {
        this.isSubmitted.set(false);
      }
    });
  }

  onChange() {
    this.solutionChange.emit({index: this.index, value: this.form.value.solution?.toString() || '0'});
  }

  removeChoice() {
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.choice_remove'),
      data: {
        label: 'global.choice_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (remove) {
        // Remove Choice
        this.slideAuthorFacade.requestRemoveChoice(this.choice, 'history.question_remove').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }
}
